import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys, QUERY_CONFIG } from '../../lib/react-query'
import { chatApi } from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import { useFeature } from '../../Utils/featureFlagEngine'
import { toast } from 'sonner'
import type { AxiosError } from 'axios'
import type { Chat, ChatMessage } from '../../app/modules/types/chatBot'
import type {
  ApprovalDecision,
  ApprovalsResponse,
  CreateChatParams,
  PageParams,
} from '../../services/api/chat.api'

/**
 * The org's chats, newest first.
 *
 * There is no total and no page count on the response (doc §10), so numbered
 * pagination is impossible: callers page with limit/offset and detect "more" by
 * asking for one extra row. `useChatListPaginated` is gone with the endpoint
 * that backed it — `ChatHistoryPage` moves onto this hook.
 *
 * Resolves a bare `Chat[]`. Django's `{results, total_pages, current_page,
 * count}` envelope is gone; a caller still destructuring it gets `undefined`.
 *
 * The params object goes into the key as an object — see the note on
 * `queryKeys.chat.list` for why pre-serialising it would be worse, not safer.
 */
/**
 * `enabled` defaults true, so every existing caller is unaffected. It exists so
 * the free-chat lookup can be switched OFF entirely rather than issuing a
 * request on every chat that is already bound.
 */
export function useChatList(params: PageParams = {}, enabled = true) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.chat.list(orgId, params),
    queryFn: () => chatApi.getList(params),
    enabled: !!orgId && enabled,
  })
}

/**
 * One chat. Needed wherever ownership matters: the approval card compares
 * `me.id` against this chat's `user_id` (doc §9), because approvals are
 * owner-only and a same-org colleague gets a 403.
 */
export function useChatDetail(chatId: string | null | undefined) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.chat.detail(orgId, chatId || 'no-chat'),
    queryFn: () => chatApi.getChat(chatId as string),
    enabled: !!chatId && !!orgId,
    staleTime: QUERY_CONFIG.CHAT.staleTime,
  })
}

/**
 * The chats anchored to a subject — the replacement for the dead
 * `vulnerability.chat_id` field (doc §7).
 *
 * Both params must be present together or the request is a 400, so the query
 * stays disabled until they are. An unknown subject yields an empty array with
 * 200, never a 404, so an empty result means "no chat yet", not "error" — it is
 * the expected first state of every finding.
 *
 * The limit is fixed at 1 and takes no parameter, because it is NOT part of the
 * query key: a subject's chats are one cache entry however many rows were asked
 * for, so two mounted callers passing 1 and 5 would silently serve each other's
 * results. §7 offers a second arm — render a list when a finding has several
 * chats, requesting `limit=5` — and choosing it means adding the limit to the
 * key at that point, deliberately, rather than leaving a parameter here that
 * looks safe to vary and is not.
 *
 * Gated by `vulnerabilities_chat`, the same flag the Bot buttons sit behind.
 * This lookup is mounted by the FINDING surfaces rather than by any chat UI —
 * `useFindingChatBinding` runs it as soon as a finding is opened, before the
 * user has asked for a chat and whether or not one exists — so without the gate
 * a deployment with the feature off, or a user without `api:chatbot:view`, still
 * issued it on every vulnerability they opened. `/chat` is not on the axios
 * ignore list, so the resulting 403 surfaced as a generic error toast about a
 * feature with no visible UI on the page. The gate lives here rather than at the
 * call sites so it covers every observer of this query, `ChatInner`'s included.
 */
export function useChatsForSubject(
  subjectType: string | null | undefined,
  subjectId: string | null | undefined,
) {
  const orgId = useAuthStore((state) => state.orgId)
  const { isEnabled } = useFeature()
  const enabled = !!orgId && !!subjectType && !!subjectId && isEnabled('vulnerabilities_chat')
  return useQuery({
    queryKey: queryKeys.chat.bySubject(orgId, subjectType ?? '', subjectId ?? ''),
    queryFn: () => chatApi.listBySubject(subjectType as string, subjectId as string, { limit: 1 }),
    enabled,
    // A colleague's new chat on the same finding must not stay invisible for the
    // ten minutes the global default would cache this for.
    staleTime: QUERY_CONFIG.CHAT.staleTime,
  })
}

/**
 * Message history for a chat, oldest first.
 *
 * Resolves the rows plainly. It used to wrap them as `{chatId, messages}` so a
 * consumer could prove which chat it was rendering, but the chat id is already
 * IN the query key: switching chats switches cache entry, and since nothing
 * sets `placeholderData` or `keepPreviousData`, `data` is `undefined` across
 * the switch rather than the previous chat's rows. The wrapper guarded against
 * something the key makes impossible.
 */
export function useChatMessages(chatId: string | null | undefined) {
  const orgId = useAuthStore((state) => state.orgId)

  return useQuery({
    queryKey: queryKeys.chat.messages(orgId, chatId || 'no-chat'),
    queryFn: (): Promise<ChatMessage[]> => chatApi.getMessages(chatId as string),
    enabled: !!chatId && !!orgId,
    staleTime: QUERY_CONFIG.CHAT.staleTime,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  })
}

/**
 * Send a user message. Resolves with the PERSISTED message (real id and
 * timestamp), which the composer renders directly instead of an optimistic
 * placeholder. The assistant turn arrives over the stream.
 */
export function useSendMessage() {
  const orgId = useAuthStore((state) => state.orgId)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ chatId, content }: { chatId: string; content: string }): Promise<ChatMessage> =>
      chatApi.sendMessage(chatId, content),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.chat.messages(orgId, variables.chatId) })
    },
  })
}

/**
 * Create a chat, free or anchored to a subject.
 *
 * The subject IS validated synchronously on create (unlike on the filter), so
 * an unknown or out-of-org `subject_id` fails with 400 — the global axios
 * interceptor surfaces it, and we deliberately do not swallow it and open an
 * empty chat (doc §14).
 *
 * Invalidating `chat.all` is enough to republish the subject lookup too: every
 * chat key starts `[orgId, 'chat', …]` and react-query matches queryKeys by
 * prefix, so `bySubject` is covered without a second, redundant call.
 *
 * Enough, but not immediate — and this ONE entry has to be. `chat.bySubject` is
 * what `resolveChatForFinding` binds the AI assistant from, and `chats[0]` there
 * beats the already-bound id unconditionally. Between this mutation resolving
 * and the refetch landing, a stale non-empty response therefore rebinds the user
 * to the OLDER chat on the same finding — the service dropped Django's
 * one-per-finding constraint, so there can be one — yanking them out of the chat
 * they just created, and flips them back when the invalidation arrives.
 *
 * So the created row is also written straight into that entry. It is the
 * server's own row, not an optimistic guess, so a later refetch can only return
 * the same thing — the same reasoning that makes `useSendFeedback`'s
 * write-through into `chat.detail` safe. Wrapped in an array because
 * `useChatsForSubject` resolves `Chat[]`.
 *
 * Guarded on both subject fields: a free chat has no subject, and writing one
 * anyway would file the row under `('', '')`, a key no real lookup can request
 * (`useChatsForSubject` stays disabled without both params) and nothing ever
 * clears. The fields come off the RESPONSE rather than the request params, so a
 * server that normalises or rejects a subject cannot leave a phantom entry
 * behind under the id we happened to ask for.
 */
export function useCreateChat() {
  const orgId = useAuthStore((state) => state.orgId)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params?: CreateChatParams): Promise<Chat> => chatApi.create(params),
    onSuccess: (chat) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.chat.all(orgId) })
      if (chat.subject_type && chat.subject_id) {
        // After the invalidation, not before: an invalidation only marks an
        // entry stale, so the fresh row survives it and is served immediately
        // to the next observer while the background refetch confirms it.
        queryClient.setQueryData(
          queryKeys.chat.bySubject(orgId, chat.subject_type, chat.subject_id),
          [chat],
        )
      }
      toast.success('Chat created successfully')
    },
  })
}

/**
 * Feedback now returns the updated chat, so `is_helpful` renders from it.
 *
 * The response is the server's own row, not an optimistic guess, so writing it
 * straight into the detail cache is safe: a later refetch can only return the
 * same persisted value.
 *
 * It updates `chat.detail` ONLY. The same chat row is also cached under
 * `chat.list` and `chat.bySubject`, and those copies keep a stale `is_helpful`
 * until they refetch. Nothing renders the flag from either today; the fix if
 * something ever does is to update those entries, not to invalidate `chat.all`
 * and drag every mounted message list into a refetch over a thumbs click.
 */
export function useSendFeedback() {
  const orgId = useAuthStore((state) => state.orgId)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ chatId, isHelpful }: { chatId: string; isHelpful: boolean }): Promise<Chat> =>
      chatApi.sendFeedback(chatId, isHelpful),
    onSuccess: (chat) => {
      queryClient.setQueryData(queryKeys.chat.detail(orgId, chat.id), chat)
    },
  })
}

/**
 * Decide every gated call on one assistant message, in one request.
 *
 * Owner-only (403 for a same-org colleague) and idempotent (409 means the call
 * was already decided — refetch and re-render, do not toast an error). 404
 * likewise means the card is stale, not that anything failed. Refetching on any
 * settle covers all three, and the wasted GET after a 403 is cheaper than
 * enumerating status codes here.
 *
 * Nothing is caught, and no status is mapped to a message here: `/approvals` is
 * exempt from the global toast AND from the forbidden-redirect in
 * `SetupAxios.tsx`, so this mutation's rejection is the ONLY signal that
 * anything went wrong. The caller owns rendering it, and an unhandled
 * `mutateAsync` here is completely silent (doc §12, §14).
 *
 * The error is typed as an `AxiosError` rather than left as `Error` precisely
 * so the caller can branch without a cast — §12 needs it to tell a 403
 * "not the owner" from a 403 "lacks the permission", and to treat 409 and 404
 * as already-handled rather than as failures.
 */
export function useDecideApprovals() {
  const orgId = useAuthStore((state) => state.orgId)
  const queryClient = useQueryClient()

  return useMutation<
    ApprovalsResponse,
    AxiosError<{ detail?: string; message?: string }>,
    { chatId: string; decisions: ApprovalDecision[] }
  >({
    mutationFn: ({ chatId, decisions }) => chatApi.decideApprovals(chatId, decisions),
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.chat.messages(orgId, variables.chatId) })
    },
  })
}
